import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Add, Refresh, Remove, FileDownload } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ControlButton, ControlProps, useReactFlow } from 'reactflow';

const Wrapper = styled.div`
  & button {
    background-color: ${({ theme }) => theme.palette.common.gray3};

    & svg {
      width: 18px;
      height: 18px;
      max-height: 18px;
      max-width: 18px;
    }

    width: 38px;
    height: 38px;
    border: 0px;

    border-radius: inherit;
  }

  border-radius: 6px;
  border: ${({ theme }) => `1px solid ${theme.palette.common.gray1}`};

  position: absolute;
  top: 24px;
  right: 24px;

  & .react-flow__controls {
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: all;

    & > *:first-child {
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
    }

    & > *:last-child {
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  z-index: 4;
`;

type Props = ControlProps & {
  onExport: () => void;
  enableExport: boolean;
};

const LineageGraphControls = ({
  showZoom = true,
  showFitView = true,
  enableExport = true,
  fitViewOptions,
  onZoomIn,
  onZoomOut,
  onFitView,
  onExport,
}: Props) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const onZoomInHandler = useCallback(() => {
    zoomIn?.();
    onZoomIn?.();
  }, [onZoomIn, zoomIn]);

  const onZoomOutHandler = useCallback(() => {
    zoomOut?.();
    onZoomOut?.();
  }, [onZoomOut, zoomOut]);

  const onFitViewHandler = useCallback(() => {
    fitView?.(fitViewOptions);
    onFitView?.();
  }, [fitView, fitViewOptions, onFitView]);

  if (!isVisible) {
    return null;
  }

  return (
    <Wrapper>
      <div className="react-flow__controls">
        {showZoom && (
          <>
            <ControlButton
              onClick={onZoomInHandler}
              title="zoom in"
              aria-label="zoom in"
            >
              <Add />
            </ControlButton>
            <ControlButton
              onClick={onZoomOutHandler}
              title="zoom out"
              aria-label="zoom out"
            >
              <Remove />
            </ControlButton>
          </>
        )}
        {showFitView && (
          <ControlButton
            onClick={onFitViewHandler}
            title="fit view"
            aria-label="fit view"
          >
            <Refresh sx={{ transform: 'scaleX(-1)' }} />
          </ControlButton>
        )}

        <Tooltip
          title={
            enableExport
              ? null
              : 'Select a node or a column to export its lineage'
          }
          placement="left"
        >
          <span>
            <ControlButton
              disabled={!enableExport}
              onClick={onExport}
              title="export"
              aria-label="export"
            >
              <FileDownload />
            </ControlButton>
          </span>
        </Tooltip>
      </div>
    </Wrapper>
  );
};

export default LineageGraphControls;
